import { keyBy, indexOf, sortBy, last, zipObject } from 'lodash-es'
import { useRef, useEffect } from 'react'
import { extent } from 'd3-array'
import { scaleSqrt, scaleLinear } from 'd3-scale'
import { CHALLENGES_COLORS, CHALLENGES, Challenge } from '../../lib/constants'
import { datasetSortedByEmPerc } from './data'

const MARGINS = { top: 50, right: 2, bottom: 2, left: 2 }

function computeDatasetWithDrawInfo({
  x,
  y,
  w,
  h,
  challenge,
  animationRadius,
  animationPosition,
}: {
  x: number
  y: number
  w: number
  h: number
  challenge: Challenge
  animationRadius: number
  animationPosition: number
}) {
  const vizContainerInfo = {
    x: x + MARGINS.left,
    y: y + MARGINS.top,
    w: w - MARGINS.left - MARGINS.right,
    h: h - MARGINS.top - MARGINS.bottom,
  }

  // create scales
  const maxEmissionPerc = last(datasetSortedByEmPerc).emission_percentage
  const radiusDomain = extent(
    datasetSortedByEmPerc.map((d) => d.emission_percentage / maxEmissionPerc)
  )
  const maxRadius = Math.min(vizContainerInfo.w, vizContainerInfo.h) / 2
  const minRadius = Math.max(30, maxRadius * 0.35) // TODO: check with designers
  const radiusScale = scaleSqrt().domain(radiusDomain).range([minRadius, maxRadius])

  const horizontalOffsetScale = scaleLinear()
    .domain([0, datasetSortedByEmPerc.length - 1]) // scale by index
    .range([0, vizContainerInfo.w - radiusScale.range()[1] * 2])

  const animationHorizontalOffset = vizContainerInfo.w / 2
  const positionScale = scaleLinear().domain([0, 1]).range([animationHorizontalOffset, 0])

  // get the main challenge index
  const mainChallengeIndex = indexOf(
    datasetSortedByEmPerc.map((d) => d.sector),
    challenge
  )

  // compute the info needed to draw the circles
  const datasetWithDrawInfo = datasetSortedByEmPerc.map((d, i) => {
    const isMainChallenge = i === mainChallengeIndex
    const isOnTheLeftOfMainChallenge = i > mainChallengeIndex

    const endRadius = radiusScale(d.emission_percentage / maxEmissionPerc)
    const currentRadius = isMainChallenge ? endRadius * animationRadius : endRadius

    const offset = isMainChallenge
      ? 0
      : isOnTheLeftOfMainChallenge
      ? positionScale(animationPosition)
      : -positionScale(animationPosition)
    const cx = vizContainerInfo.x + endRadius + horizontalOffsetScale(i) + offset

    return {
      ...d,
      x: cx,
      y: vizContainerInfo.y + vizContainerInfo.h - endRadius,
      radius: currentRadius,
      opacity: isMainChallenge ? 1 : animationPosition ** 3,
      color: CHALLENGES_COLORS[d.sector],
      label: d.sector,
      percentage: d.emission_percentage,
      index: i,
    }
  })
  return datasetWithDrawInfo.reverse()
}

export const useCircleChallengesDrawInfo = (
  canvasWidth: number,
  canvasHeight: number,
  imageWidth: number,
  imageHeight: number,
  challenge: Challenge
) => {
  const positions = useRef({
    canvasWidth,
    canvasHeight,
    imageWidth,
    imageHeight,
    challenge: challenge,
    animationRadius: 0,
    animationPosition: 0,
    animationFillOpacity: zipObject(
      CHALLENGES,
      CHALLENGES.map((c) => (c === challenge ? 1 : 0))
    ),

    get datasetWithDrawInfo() {
      const dataset = computeDatasetWithDrawInfo({
        x: (this.canvasWidth - this.imageWidth) / 2,
        y: 0,
        w: this.imageWidth,
        h: this.imageHeight,
        challenge: this.challenge,
        animationRadius: this.animationRadius,
        animationPosition: this.animationPosition,
      })
      return keyBy(dataset, 'sector')
    },

    getHoveredCircleChallenge(xPos: number, yPos: number) {
      const circles = sortBy(Object.values(this.datasetWithDrawInfo), 'x')
      return circles.find((c) => Math.hypot(c.x - xPos, c.y - yPos) < c.radius)?.sector
    },
  }).current

  useEffect(() => {
    positions.canvasWidth = canvasWidth
    positions.canvasHeight = canvasHeight
    positions.imageWidth = imageWidth
    positions.imageHeight = imageHeight
    positions.challenge = challenge
  }, [canvasWidth, canvasHeight, imageWidth, imageHeight, challenge])

  return positions
}
